import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type DomainVerificationStatus = 'unverified' | 'verified' | 'failed';
export type DomainVerificationMethod = 'dns' | 'html_meta_tag';

export interface DnsTxtInstructions {
  recordType: string;
  host: string;
  value: string;
}

export interface DomainVerificationInstructions {
  dns: DnsTxtInstructions;
  metaTag: string;
}

export interface Site {
  id: string;
  domain: string;
  displayName: string;
  domainVerified: boolean;
  consentVersion: string;
  verificationToken?: string;
  verificationStatus?: DomainVerificationStatus;
  verifiedVia?: DomainVerificationMethod | null;
  verifiedAt?: string | null;
  lastAttemptAt?: string | null;
  lastVerificationError?: string | null;
  instructions?: DomainVerificationInstructions;
  /**
   * Whether the current user may mutate this site. False for an agency Viewer. Optional so an
   * older API response still parses; callers default it to true rather than locking an owner out.
   */
  canEdit?: boolean;
  /**
   * Whether the current user may delete this site. Owner-only, a stricter bar than `canEdit`, which
   * an agency Member also clears. Optional so an older API response still parses; callers default
   * it to **false** rather than true — offering a delete the API refuses is worse than hiding one
   * an owner could have used.
   */
  canDelete?: boolean;
  /** Banner locale code, or null/absent to follow the visitor's browser. */
  defaultLanguage?: string | null;
  /** Rolling per-page verdict (A15): how many registered pages last came back OK / failed. */
  pagesOk?: number;
  pagesFailed?: number;
  /** True when at least one page's latest scan failed. */
  hasProblem?: boolean;
}

export interface VerifyDomainResult {
  verified: boolean;
  status: DomainVerificationStatus;
  verifiedVia?: DomainVerificationMethod | null;
  error?: string | null;
  /** Only present on a 429 cooldown response; seconds remaining before the next re-check is allowed. */
  retryAfterSeconds?: number | null;
}

export interface CreateSiteResponse {
  siteId: string;
  domain: string;
  verificationToken: string;
}

const LAST_SITE_KEY = 'privaci_last_site';

function readLastSite(): string | null {
  try {
    return localStorage.getItem(LAST_SITE_KEY);
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class SitesService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/sites`;

  private readonly _sites = signal<Site[]>([]);
  readonly sites = this._sites.asReadonly();

  // Last site the user was actually in. Root-scoped and persisted so it survives the app-shell being
  // torn down and re-created when moving between per-site and account-scoped route groups.
  private readonly _lastSiteId = signal<string | null>(readLastSite());
  readonly lastSiteId = this._lastSiteId.asReadonly();

  setLastSiteId(id: string): void {
    this._lastSiteId.set(id);
    try {
      localStorage.setItem(LAST_SITE_KEY, id);
    } catch {
      // localStorage unavailable (private mode / disabled) - the in-memory signal still works.
    }
  }

  loadSites() {
    return this.http.get<Site[]>(this.apiUrl).pipe(
      tap(list => this._sites.set(list)),
    );
  }

  createSite(domain: string, displayName: string) {
    return this.http.post<CreateSiteResponse>(this.apiUrl, { domain, displayName });
  }

  getSite(siteId: string) {
    return this.http.get<Site>(`${this.apiUrl}/${siteId}`);
  }

  /**
   * Domain is not updatable: changing it would invalidate the ownership proof, so the API's command
   * does not carry it. `defaultLanguage` is null for "follow the visitor's browser".
   */
  updateSite(
    siteId: string,
    displayName: string,
    consentVersion?: string,
    defaultLanguage?: string | null,
  ) {
    return this.http.put<Site>(`${this.apiUrl}/${siteId}`, {
      displayName,
      consentVersion,
      defaultLanguage: defaultLanguage || null,
    });
  }

  verifyDomain(siteId: string, method: DomainVerificationMethod) {
    return this.http.post<VerifyDomainResult>(`${this.apiUrl}/${siteId}/verify-domain`, { method });
  }

  deleteSite(siteId: string) {
    return this.http.delete(`${this.apiUrl}/${siteId}`);
  }
}
